// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * PlaceField — zoekbaar plaatsveld.
 *
 * Toont tijdens het typen passende suggesties uit de complete lijst Nederlandse
 * woonplaatsen (`NL_PLACES`). De gebruiker kiest uit de suggesties óf typt een
 * eigen plaats; de waarde wordt in één veld opgeslagen. Daardoor:
 *  - werkt het op mobiel prettig (geen dropdown met duizenden regels);
 *  - blijft elke plaats invoerbaar (ook buurtschappen die niet in de lijst staan);
 *  - blijft bestaande vrije invoer behouden en tonen exports zonder aanpassing de juiste waarde.
 */
import { useMemo, useRef, useState, useEffect } from 'react'
import { Input } from '@/components/ui/Input'
import { NL_PLACES } from '@/lib/constants/places'

interface PlaceFieldProps {
  label?:       string
  value:        string
  onChange:     (value: string) => void
  placeholder?: string
  hint?:        string
  /** Maximaal aantal getoonde suggesties. */
  maxResults?:  number
}

export function PlaceField({
  label,
  value,
  onChange,
  placeholder = 'Typ een plaats...',
  hint,
  maxResults = 8,
}: PlaceFieldProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const q = value.trim().toLowerCase()

  const matches = useMemo(() => {
    if (q.length < 1) return []
    const starts: string[] = []
    const contains: string[] = []
    for (const p of NL_PLACES) {
      const pl = p.toLowerCase()
      if (pl.startsWith(q)) starts.push(p)
      else if (pl.includes(q)) contains.push(p)
    }
    return [...starts, ...contains].slice(0, maxResults)
  }, [q, maxResults])

  // Verberg de lijst als de waarde exact één suggestie is (al gekozen).
  const exact = matches.length === 1 && matches[0].toLowerCase() === q
  const showList = open && matches.length > 0 && !exact

  return (
    <div ref={wrapRef} className="relative">
      <Input
        label={label}
        value={value}
        placeholder={placeholder}
        hint={hint}
        autoComplete="off"
        onChange={e => { onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
      />
      {showList && (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 w-full max-h-60 overflow-auto rounded-xl border border-sand-300 bg-white shadow-lg"
        >
          {matches.map(p => (
            <li key={p}>
              <button
                type="button"
                className="block w-full text-left px-4 py-2.5 text-sm text-ink hover:bg-sage-50 active:bg-sage-100"
                onMouseDown={e => { e.preventDefault(); onChange(p); setOpen(false) }}
              >
                {p}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
